import { Stack, useLocalSearchParams } from "expo-router"
import { useCombinedLoanData, useEditLoan } from "@/queries/loan"
import {
  type LoanApplication,
  LoanContext,
} from "@/screens/loans/components/LoanContext"
import FullScreenLoader from "@/components/loading/FullScreenLoader"
import BFText from "@/components/standard/BFText"
import { useCallback, useEffect } from "react"
import { type LoanApplicationInput } from "@/generated/graphql"
import { showErrorToast } from "@/components/toasts/AppToast"
import { loanAppErrorEncountered } from "@/analytics/events/loans"
import type { ClientError } from "graphql-request"
import { useUser } from "@/queries/user"
import { useStore } from "@nanostores/react"
import {
  addLoanUpdates,
  getLoanUpdateStore,
  pushToServer,
  setLoanUpdates,
  updatedFromServer,
} from "@/stores/loanState"
import { hasRewards } from "@/utils/UserUtils"

export default () => {
  const { loanId } = useLocalSearchParams()
  const { mutate: updateLoanMutation } = useEditLoan()
  const singleLoanId = Array.isArray(loanId) ? loanId[0] : loanId
  const loanUpdateState = useStore(getLoanUpdateStore(singleLoanId))
  const {
    data: loan,
    isLoading,
    isError,
    refetch: refetchLoanData,
  } = useCombinedLoanData(singleLoanId)

  const updates = loanUpdateState.updates
  const { data: userData } = useUser()
  const { id: userId, firstName, lastName } = userData?.me || {}

  useEffect(() => {
    updatedFromServer(singleLoanId)
  }, [loan])

  const update = (u: LoanApplicationInput) => {
    addLoanUpdates(singleLoanId, u)
  }

  const save = async () => {
    await new Promise<void>((resolve, reject) => {
      const updatesToServer = pushToServer(singleLoanId) // updateRef.current
      console.log(
        `loan(${singleLoanId}): save(${JSON.stringify(updatesToServer)}`
      )

      updateLoanMutation(
        {
          loanId: singleLoanId,
          loanInput: updatesToServer,
        },
        {
          onSuccess: (data) => {
            console.log(
              `loan(${singleLoanId}): save.onSuccess(${JSON.stringify(data)}`
            )
            resolve()
          },
          onError: (err) => {
            addLoanUpdates(singleLoanId, updatesToServer)
            const displayMessage =
              "Please give us a call at 844.363.5477 to continue."
            showErrorToast({
              title: "Loan Processing Error",
              message: displayMessage,
            })
            loanAppErrorEncountered({
              error: err as ClientError,
              displayedMessage: displayMessage,
              propertyAddress:
                loan?.loanApplication?.property.fullAddress || "Unknown",
              userId: String(userId),
              userName: `${firstName} ${lastName}`,
            })
            reject(err)
          },
        }
      )
    })
  }

  const clear = useCallback(() => {
    setLoanUpdates(singleLoanId, {})
  }, [singleLoanId])

  if (isLoading) {
    return <FullScreenLoader dark={true}>Loading...</FullScreenLoader>
  }

  if (isError || !loanId || !loan?.loanApplication) {
    return <BFText>Error loading loan details. Please try again later.</BFText>
  }

  if (!userData?.me) {
    // Need something better here .. IF this happens does it mean logged out?
    return null
  }

  // Compute userHasRewards based on userData
  const userHasRewards = hasRewards(userData.me)

  const loanApplication = {
    ...loan.loanApplication,
    ...loanUpdateState.sentToServer.reduce<LoanApplicationInput>(
      (acc, obj) => ({
        ...acc,
        ...obj,
      }),
      {}
    ),
    ...updates,
  }

  return (
    <LoanContext.Provider
      value={{
        loanApplication: loanApplication as LoanApplication,
        capitalProducts: loan.capitalProducts!,
        loanId: singleLoanId,
        update,
        save,
        clear,
        refetch: async () => {
          await refetchLoanData()
        },
        userHasRewards,
        isLoading: false,
        isError: false,
        cashToCloseDisplaysAsRange:
          !userHasRewards && loanApplication?.loanProduct !== "refi",
      }}
    >
      <Stack screenOptions={{ headerShown: false }}>
        <Stack.Screen
          name="index"
          options={{ headerShown: false, animation: "fade" }}
        />
        <Stack.Screen
          name="softcreditpull"
          options={{
            headerShown: false,
          }}
        />
      </Stack>
    </LoanContext.Provider>
  )
}
